// StrangerDetection.js
import Modal from 'components/ui/modal';
import { useAuth } from 'components/ui/navigationGuard';
import Notification from 'components/ui/notification';
import useStrangerDetectionLogic from 'hooks/useDetection';
import AdminNavigation from 'layouts/adminNavigation';
import EmployeeAttendanceModal from 'pages/attendance-records/components/employeeAttendanceModal';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/appIcon';
import Button from '../../components/ui/button';
import BulkActionsBar from './components/bulkActionsBar';
import DetectionTabs from './components/detectionTabs';
import FilterPanel from './components/filterPanel';
import StrangerTableRow from './components/strangerCard';
import StrangerDetailsModal from './components/strangerDetailsModal';
import SwitchStrangerModal from './components/switchStrangerModal';

const StrangerDetection = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Use the custom hook to get all the state and functions
  const {
    isNavCollapsed,
    filtersCollapsed,
    selectedStrangers,
    detailsModal,
    timeFilter,
    attendanceModal,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    strangers,
    loading,
    notification,
    cameras,
    activeTab,
    viewMode,
    detectionCounts,
    stats,
    filters,
    filteredStrangers,
    paginatedStrangers,
    totalPages,
    startIndex,
    setIsNavCollapsed,
    setFiltersCollapsed,
    setSelectedStrangers,
    setDetailsModal,
    setTimeFilter,
    setAttendanceModal,
    setCurrentPage,
    setSortBy,
    setSortOrder,
    setActiveTab,
    setViewMode,
    setFilters,
    closeNotification,
    showNotification,
    loadCameras,
    loadStrangers,
    fetchEmployeeAttendanceSummary,
    fetchEmployeeAttendanceRecords,
    handleTimeFilterChange,
    handleStrangerSelect,
    handleSelectAll,
    handleIdentify,
    handleMarkAsVisitor,
    handleDismiss,
    handleActivate,
    handleBulkIdentify,
    handleBulkMarkAsVisitor,
    handleBulkDismiss,
    handleBulkActivate,
    handleBulkExport,
    handleViewDetails,
    handleViewAttendance,
    handleAttendanceTimeFilterChange,
    handleAddNotes,
    handleFiltersChange,
    handleResetFilters,
    handleSortChange,
    handleBulkDelete,
    switchModal,
    setSwitchModal,
    handleSwitchStranger,
  } = useStrangerDetectionLogic();

  // Loading state
  if (loading) {
    return (
      <>
        <AdminNavigation
          user={user}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
        />
        <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 transition-all duration-200 ${isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16`}>
          <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-lg font-medium text-slate-700">Loading stranger detections...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavigation
        user={user}
        isCollapsed={isNavCollapsed}
        onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
      />
      <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 transition-all duration-200 ${isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16`}>

        <main className="p-4 md:p-6">
          {/* Page Header */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-800">Stranger Detection</h1>
                <p className="text-slate-500 mt-2">
                  Monitor and manage unrecognized faces detected by CCTV system
                </p>
              </div>
            </div>
          </div>

          {/* Detection Class Tabs */}
          {/* <div className="bg-white rounded-xl shadow-sm p-2 mb-6 border border-slate-200">
            <DetectionTabs activeTab={activeTab} setActiveTab={setActiveTab} detectionCounts={detectionCounts} />
          </div> */}

          {/* Filters */}
          {/* <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-slate-200"> */}
          <FilterPanel
            filters={filters}
            cameras={cameras}
            onFiltersChange={handleFiltersChange}
            onReset={handleResetFilters}
            isCollapsed={filtersCollapsed}
            onToggle={() => setFiltersCollapsed(!filtersCollapsed)}
            handleTimeFilterChange={handleTimeFilterChange}
            timeFilter={timeFilter}
          />
          {/* </div> */}

          {/* Results Header */}
          {/* <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-slate-800">
                  Detection Results <span className="text-blue-600">({filteredStrangers?.length})</span>
                </h2>
              </div>
            </div>
          </div> */}

          {/* Display content based on view mode */}
          {viewMode === 'table' ? (
            /* Stranger Table */
            paginatedStrangers?.length > 0 ? (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-200 mb-8">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedStrangers?.length === paginatedStrangers?.length && paginatedStrangers?.length > 0}
                            onChange={handleSelectAll}
                            className="w-4 h-4 text-primary bg-card border-border rounded focus:ring-primary"
                          />
                        </th>
                        <th className="p-3 text-left">Image</th>
                        <th className="p-3 text-left">ID</th>
                        <th className="p-3 text-left">Name</th>
                        <th className="p-3 text-left">Location</th>
                        <th className="p-3 text-left">Detected At</th>
                        <th className="p-3 text-left">Status</th>
                        <th className="p-3 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedStrangers?.map((stranger) => (
                        <StrangerTableRow
                          key={stranger?.id}
                          stranger={stranger}
                          onIdentify={handleIdentify}
                          onMarkAsVisitor={handleMarkAsVisitor}
                          onDismiss={handleDismiss}
                          onActivate={handleActivate}
                          onViewDetails={handleViewDetails}
                          onViewAttendance={handleViewAttendance}
                          isSelected={selectedStrangers?.includes(stranger?.id)}
                          onSelect={handleStrangerSelect}
                          onSwitch={(stranger) => setSwitchModal({ isOpen: true, stranger })}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-slate-200">
                <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Eye" size={36} className="text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3">No Detections Found</h3>
                <p className="text-slate-500 mb-6 max-w-md mx-auto">
                  No stranger detections match your current filters. Try adjusting your filter criteria.
                </p>
                <Button
                  variant="outline"
                  onClick={handleResetFilters}
                  className="px-5 py-2.5 rounded-lg border-slate-300 hover:bg-slate-50"
                >
                  Clear Filters
                </Button>
              </div>
            )
          ) : (
            /* Gallery View */
            <></>
          )}
          <SwitchStrangerModal
            stranger={switchModal.stranger}
            isOpen={switchModal.isOpen}
            onClose={() => setSwitchModal({ isOpen: false, stranger: null })}
            onSuccess={handleSwitchStranger}
          />
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200">
              <div className="text-sm text-slate-600">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredStrangers?.length)} of {filteredStrangers?.length} results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  iconName="ChevronLeft"
                  iconPosition="left"
                  className="px-3 py-1.5 rounded-lg border-slate-300 hover:bg-slate-50"
                >
                  Previous
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(1, totalPages) }, (_, i) => {
                    // Calculate page numbers to show: current page in center with 2 before and 2 after
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = Math.min(totalPages, startPage + 4);

                    // Adjust if we're near the end
                    if (endPage - startPage < 4) {
                      startPage = Math.max(1, endPage - 4);
                    }

                    // Generate page numbers to display
                    const pages = [];
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(i);
                    }

                    return pages.map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 rounded-lg ${currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-slate-100'}`}
                      >
                        {page}
                      </Button>
                    ));
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  iconName="ChevronRight"
                  iconPosition="right"
                  className="px-3 py-1.5 rounded-lg border-slate-300 hover:bg-slate-50"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedStrangers?.length}
        onBulkIdentify={handleBulkIdentify}
        onBulkMarkAsVisitor={handleBulkMarkAsVisitor}
        onBulkDismiss={handleBulkDismiss}
        onBulkActivate={handleBulkActivate}
        onBulkExport={handleBulkExport}
        onClearSelection={() => setSelectedStrangers([])}
        handleBulkDelete={handleBulkDelete}
      />

      {/* Details Modal */}
      <Modal isOpen={detailsModal?.isOpen} onClose={() => setDetailsModal({ isOpen: false, stranger: null })}>
        <StrangerDetailsModal
          stranger={detailsModal?.stranger}
          isOpen={detailsModal?.isOpen}
          onClose={() => setDetailsModal({ isOpen: false, stranger: null })}
          onIdentify={handleIdentify}
          onMarkAsVisitor={handleMarkAsVisitor}
          onDismiss={handleDismiss}
          onActivate={handleActivate}
          onAddNotes={handleAddNotes}
        />
      </Modal>

      {/* Attendance Modal */}
      {attendanceModal.isOpen && (
        <EmployeeAttendanceModal
          employee={attendanceModal?.stranger}
          attendanceSummary={attendanceModal?.attendanceSummary}
          attendanceRecords={attendanceModal?.attendanceRecords}
          timeFilter={attendanceModal?.timeFilter}
          onTimeFilterChange={handleAttendanceTimeFilterChange}
          onClose={() => setAttendanceModal({
            isOpen: false,
            stranger: null,
            attendanceSummary: null,
            attendanceRecords: [],
            timeFilter: 'today'
          })}
        />
      )}

      {/* Notification Component */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
    </>
  );
};

export default StrangerDetection;